import type { Category } from "../types/category.dto";
import type { CategoryItem } from "../types/category-panel";
import { categoryArtwork } from "./category-artwork";
import { resolveCategoryHref } from "@/modules/widgets/lib/resolve-widget-link";

function mapCategory(category: Category, parentNames: string[] = []): CategoryItem {
    const children = category.children?.filter((c) => c.status !== 0);
    const trailNames = [...parentNames, category.name];
    const artwork = categoryArtwork(category);

    return {
        id: String(category.ecmCategoryId),
        label: category.name,
        href: resolveCategoryHref(category, parentNames),
        icon: category.icon || undefined,
        // `imageUrl` é a MINIATURA (thumb ao lado do nome), não a arte do painel.
        imageUrl: category.image || undefined,
        // Cadeia de fallback, do mais específico ao mais genérico: arte do painel
        // cadastrada no portal → os campos `imageMobile`/`imageDesktop` do DTO
        // (que o backend ainda não preenche, mantidos por compatibilidade) →
        // miniatura. Sem o primeiro nível, painel e barra lateral disputavam o
        // mesmo `image` e um dos dois saía com o recorte errado.
        panelImageMobileUrl: artwork.panelMobile || category.imageMobile || undefined,
        panelImageDesktopUrl:
            artwork.panelDesktop || category.imageDesktop || category.image || undefined,
        // Vídeo do painel: sem fallback para imagem — quem renderiza decide (vídeo
        // ganha da arte quando os dois existem; o portal grava só um).
        panelVideoMobileUrl: artwork.panelVideoMobile,
        panelVideoDesktopUrl: artwork.panelVideoDesktop,
        panelProductIds: artwork.panelProducts,
        children:
            children && children.length > 0
                ? [...children]
                      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
                      .map((child) => mapCategory(child, trailNames))
                : undefined,
    };
}

/**
 * Converte as categorias do backend (Category) nos itens do CategoryPanel.
 * Filtra inativas e ordena por sortOrder. `getRootCategories()` traz só a raiz
 * (sem children); subcategorias/marcas exigem o endpoint de filhos/árvore.
 */
export function toCategoryItems(categories: Category[]): CategoryItem[] {
    return categories
        .filter((c) => c.status !== 0)
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
        .map((category) => mapCategory(category));
}
